// Controller spine geometry. A controller's "spine" is the polyline the field
// measures distance to: [a] for a point, [a, b] for a line, the nodes for a
// polyline, and a sampled centripetal Catmull-Rom for a curve. One sampler
// feeds field evaluation, canvas hit-testing, and drawing, so the preview and
// every exporter always agree on where a controller's influence lies.

import type { Controller, DocumentModel, Vec2 } from './types'
import { CHANNEL_IDS } from './types'

export const CURVE_SUBDIV = 12 // samples per curve span, fixed and deterministic

// Centripetal Catmull-Rom (alpha 0.5) through the nodes, endpoints included
// exactly. Ghost endpoints are reflections, so the curve starts and ends at
// the first and last node heading toward its neighbor.
export function sampleCatmullRom(nodes: Vec2[], subdiv = CURVE_SUBDIV): Vec2[] {
  const n = nodes.length
  if (n < 2) return nodes.slice()
  if (n === 2) return [nodes[0], nodes[1]]

  const reflect = (p: Vec2, about: Vec2): Vec2 => ({ x: 2 * about.x - p.x, y: 2 * about.y - p.y })
  const pts = [reflect(nodes[1], nodes[0]), ...nodes, reflect(nodes[n - 2], nodes[n - 1])]

  const out: Vec2[] = [nodes[0]]
  for (let i = 1; i + 2 < pts.length; i++) {
    const p0 = pts[i - 1]
    const p1 = pts[i]
    const p2 = pts[i + 1]
    const p3 = pts[i + 2]
    // Knots by centripetal parameterization; degenerate spans get a tiny knot
    // interval so coincident nodes never divide by zero.
    const t0 = 0
    const t1 = t0 + Math.max(1e-6, Math.sqrt(Math.hypot(p1.x - p0.x, p1.y - p0.y)))
    const t2 = t1 + Math.max(1e-6, Math.sqrt(Math.hypot(p2.x - p1.x, p2.y - p1.y)))
    const t3 = t2 + Math.max(1e-6, Math.sqrt(Math.hypot(p3.x - p2.x, p3.y - p2.y)))
    for (let s = 1; s <= subdiv; s++) {
      const t = t1 + ((t2 - t1) * s) / subdiv
      out.push(barryGoldman(p0, p1, p2, p3, t0, t1, t2, t3, t))
    }
  }
  out[out.length - 1] = nodes[n - 1] // exact endpoint, no floating drift
  return out
}

function lerpPoint(a: Vec2, b: Vec2, ta: number, tb: number, t: number): Vec2 {
  if (tb - ta < 1e-12) return a
  const u = (t - ta) / (tb - ta)
  return { x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u }
}

function barryGoldman(
  p0: Vec2,
  p1: Vec2,
  p2: Vec2,
  p3: Vec2,
  t0: number,
  t1: number,
  t2: number,
  t3: number,
  t: number,
): Vec2 {
  const a1 = lerpPoint(p0, p1, t0, t1, t)
  const a2 = lerpPoint(p1, p2, t1, t2, t)
  const a3 = lerpPoint(p2, p3, t2, t3, t)
  const b1 = lerpPoint(a1, a2, t0, t2, t)
  const b2 = lerpPoint(a2, a3, t1, t3, t)
  return lerpPoint(b1, b2, t1, t2, t)
}

// Spine cache: documents are cloned on every store mutation, so controller
// objects are stable between mutations and safe WeakMap keys.
const spineCache = new WeakMap<Controller, Vec2[]>()

export function controllerSpine(c: Controller): Vec2[] {
  const cached = spineCache.get(c)
  if (cached) return cached
  let pts: Vec2[]
  switch (c.kind) {
    case 'point':
      pts = [c.a]
      break
    case 'line':
      pts = [c.a, c.b]
      break
    case 'polyline':
      pts = c.nodes.length >= 2 ? c.nodes : [c.a]
      break
    case 'curve':
      pts = c.nodes.length >= 2 ? sampleCatmullRom(c.nodes) : [c.a]
      break
  }
  spineCache.set(c, pts)
  return pts
}

export interface DistSide {
  d: number
  side: number // sign of cross(travel direction, point), 0 on the spine
}

// Distance from (x, y) to the spine, with the side of the closest segment.
// With `infinite`, the first and last segments extend to infinity. In the
// y-down document frame, walking a -> b, negative side is the walker's left.
export function spineDistSide(pts: Vec2[], x: number, y: number, infinite: boolean): DistSide {
  if (pts.length === 1) {
    return { d: Math.hypot(x - pts[0].x, y - pts[0].y), side: 0 }
  }
  let best = Infinity
  let bestSide = 0
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i]
    const b = pts[i + 1]
    const dx = b.x - a.x
    const dy = b.y - a.y
    const len2 = dx * dx + dy * dy
    if (len2 < 1e-18) continue
    let t = ((x - a.x) * dx + (y - a.y) * dy) / len2
    const first = i === 0
    const last = i + 2 === pts.length
    if (!(infinite && first) || t > 0) t = Math.max(0, t)
    if (!(infinite && last) || t < 1) t = Math.min(1, t)
    const px = a.x + t * dx
    const py = a.y + t * dy
    const d = Math.hypot(x - px, y - py)
    if (d < best) {
      best = d
      const cross = dx * (y - a.y) - dy * (x - a.x)
      bestSide = Math.sign(cross)
    }
  }
  if (best === Infinity) {
    // All segments degenerate: fall back to the first node.
    return { d: Math.hypot(x - pts[0].x, y - pts[0].y), side: 0 }
  }
  return { d: best, side: bestSide }
}

// Per-vertex offset of a spine by `dist` toward `side` (+1 or -1), using
// averaged unit normals at the joints. Used to draw single-side bands; it is
// a visual guide, not export geometry.
export function offsetSpine(pts: Vec2[], dist: number, side: number): Vec2[] {
  if (pts.length < 2) return pts.slice()
  const normals: Vec2[] = []
  for (let i = 0; i + 1 < pts.length; i++) {
    const dx = pts[i + 1].x - pts[i].x
    const dy = pts[i + 1].y - pts[i].y
    const len = Math.hypot(dx, dy) || 1
    // For side +1 the normal points toward positive cross(dir, p - a).
    normals.push({ x: (-dy / len) * side, y: (dx / len) * side })
  }
  const out: Vec2[] = []
  for (let i = 0; i < pts.length; i++) {
    const n1 = normals[Math.max(0, i - 1)]
    const n2 = normals[Math.min(normals.length - 1, i)]
    let nx = n1.x + n2.x
    let ny = n1.y + n2.y
    const len = Math.hypot(nx, ny) || 1
    nx /= len
    ny /= len
    out.push({ x: pts[i].x + nx * dist, y: pts[i].y + ny * dist })
  }
  return out
}

function near(a: Vec2, b: Vec2): boolean {
  return Math.abs(a.x - b.x) < 1e-6 && Math.abs(a.y - b.y) < 1e-6
}

function sameGeometry(a: Controller, b: Controller): boolean {
  return (
    a.kind === b.kind &&
    near(a.a, b.a) &&
    near(a.b, b.b) &&
    a.nodes.length === b.nodes.length &&
    a.nodes.every((p, i) => near(p, b.nodes[i]))
  )
}

// Turn cross-channel sync on or off for one controller. Turning it on links
// (or creates) a controller with the same geometry in every other channel;
// turning it off only detaches this controller, the others keep their group.
export function setSyncAcrossChannels(
  doc: DocumentModel,
  channel: (typeof CHANNEL_IDS)[number],
  id: string,
  on: boolean,
) {
  const src = doc.channels[channel].controllers.find((c) => c.id === id)
  if (!src) return
  if (!on) {
    src.syncGroup = null
    return
  }
  const group = src.syncGroup ?? 'g' + Math.random().toString(36).slice(2, 10)
  src.syncGroup = group
  for (const ch of CHANNEL_IDS) {
    if (ch === channel) continue
    const list = doc.channels[ch].controllers
    // Adopt a controller already in the group or one with identical geometry
    // (a re-synced former member), otherwise create a linked copy.
    const existing = list.find((c) => c.syncGroup === group) ?? list.find((c) => sameGeometry(c, src))
    if (existing) {
      existing.syncGroup = group
      continue
    }
    const copy = structuredClone(src)
    copy.id = 'c' + Math.random().toString(36).slice(2, 10)
    copy.value = ch === 'angle' ? 45 : ch === 'shape' ? 1 : doc.channels[ch].base * 1.5
    list.push(copy)
  }
}

// Copy the shared geometry (a, b, nodes, radius) of the source controller to
// every controller in its sync group across all channels. Values, falloff,
// and side options stay per-channel.
export function propagateSync(doc: DocumentModel, sourceId: string) {
  let source: Controller | null = null
  for (const id of CHANNEL_IDS) {
    const c = doc.channels[id].controllers.find((x) => x.id === sourceId)
    if (c) source = c
  }
  if (!source || !source.syncGroup) return
  for (const id of CHANNEL_IDS) {
    for (const c of doc.channels[id].controllers) {
      if (c.id === sourceId || c.syncGroup !== source.syncGroup) continue
      c.kind = source.kind
      c.a = { ...source.a }
      c.b = { ...source.b }
      c.nodes = source.nodes.map((p) => ({ ...p }))
      c.radius = source.radius
    }
  }
}
